/**
 * 📊 INDICATOR SERVICE — Inteligência Operacional
 *
 * Serviço para o módulo Indicadores:
 *  - CRUD de indicadores (cycle_time, lead_time, SLA, etc.)
 *  - Snapshots de valores históricos
 *  - Filtragem por organização, tipo, status
 *
 * Regras:
 *  - Sempre filtrar por organization_id
 *  - Nunca chamar Supabase direto em componentes
 *  - Respeitar RLS e multi-tenancy
 */

import { supabase } from '../lib/supabaseClient';
import type { Indicator, IndicatorSnapshot, IndicatorType, IndicatorStatus } from '../types/indicator';

export interface IndicatorFilters {
  type?: IndicatorType;
  status?: IndicatorStatus;
  project_id?: string;
  workflow_id?: string;
  issue_id?: string;
  search?: string;
}

export class IndicatorService {
  static async list(organizationId: string, filters?: IndicatorFilters): Promise<Indicator[]> {
    let query = supabase
      .from('indicators')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (filters?.type) query = query.eq('type', filters.type);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.project_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.project_id)) query = query.eq('project_id', filters.project_id);
    if (filters?.issue_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(filters.issue_id)) query = query.eq('issue_id', filters.issue_id);
    if (filters?.workflow_id) query = query.eq('workflow_id', filters.workflow_id);
    if (filters?.search) query = query.ilike('name', `%${filters.search}%`);

    const { data, error } = await query;
    if (error) {
      console.warn('[IndicatorService.list] Query failed (migration pending?):', error.message);
      return [];
    }
    return (data ?? []) as Indicator[];
  }

  static async getById(organizationId: string, indicatorId: string): Promise<Indicator | null> {
    const { data, error } = await supabase
      .from('indicators')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', indicatorId)
      .single();

    if (error) throw error;
    return data as Indicator | null;
  }

  static async create(organizationId: string, indicator: Partial<Omit<Indicator, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>): Promise<Indicator> {
    const { data, error } = await supabase
      .from('indicators')
      .insert({ ...indicator, organization_id: organizationId })
      .select()
      .single();

    if (error) throw error;
    return data as Indicator;
  }

  static async update(organizationId: string, indicatorId: string, updates: Partial<Indicator>): Promise<Indicator> {
    const { data, error } = await supabase
      .from('indicators')
      .update(updates)
      .eq('organization_id', organizationId)
      .eq('id', indicatorId)
      .select()
      .single();

    if (error) throw error;
    return data as Indicator;
  }

  static async delete(organizationId: string, indicatorId: string): Promise<void> {
    const { error } = await supabase
      .from('indicators')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', indicatorId);

    if (error) throw error;
  }

  static async recordSnapshot(organizationId: string, indicatorId: string, value: number): Promise<void> {
    const { error: snapError } = await supabase
      .from('indicator_snapshots')
      .insert({ indicator_id: indicatorId, organization_id: organizationId, value });

    if (snapError) throw snapError;

    // Update current_value on indicator
    const indicator = await this.getById(organizationId, indicatorId);
    if (indicator) {
      await this.update(organizationId, indicatorId, {
        previous_value: indicator.current_value,
        current_value: value,
      });
    }
  }

  static async getSnapshots(organizationId: string, indicatorId: string, limit = 100): Promise<IndicatorSnapshot[]> {
    const { data, error } = await supabase
      .from('indicator_snapshots')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('indicator_id', indicatorId)
      .order('recorded_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data ?? []) as IndicatorSnapshot[];
  }

  static async getLatestSnapshotMap(
    organizationId: string,
    indicatorIds: string[],
  ): Promise<Record<string, string>> {
    if (indicatorIds.length === 0) return {};

    const { data, error } = await supabase
      .from('indicator_snapshots')
      .select('indicator_id, recorded_at')
      .eq('organization_id', organizationId)
      .in('indicator_id', indicatorIds)
      .order('recorded_at', { ascending: false });

    if (error) throw error;

    const latestByIndicator: Record<string, string> = {};
    for (const row of data ?? []) {
      const indicatorId = row.indicator_id as string;
      if (!latestByIndicator[indicatorId]) {
        latestByIndicator[indicatorId] = row.recorded_at as string;
      }
    }

    return latestByIndicator;
  }
}
